import { useState } from 'react';
import { Pencil, Save, X } from 'lucide-react';
import type { UserDetail } from '../UserDetailPage';

interface Props {
  user: UserDetail;
  isEditing: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (updates: Partial<UserDetail>) => Promise<void>;
  onHardDelete: () => void;
}

// Extracted outside the component to prevent remount/focus loss on every keystroke
function RankingField({ label, value, formValue, field, description, isEditing, onChange }: {
  label: string;
  value: string;
  formValue: string;
  field: string;
  description?: string;
  isEditing: boolean;
  onChange: (field: string, value: string) => void;
}) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
      <label className="block text-sm font-medium text-slate-400 mb-1">{label}</label>
      {description && <p className="text-xs text-slate-500 mb-2">{description}</p>}
      {isEditing ? (
        <input
          type="text"
          value={formValue}
          onChange={e => onChange(field, e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
          placeholder="Not set"
        />
      ) : (
        <div className="text-lg font-semibold text-white">{value || '\u2014'}</div>
      )}
    </div>
  );
}

export function UserRankingsTab({ user, isEditing, isSaving, onEdit, onCancel, onSave, onHardDelete }: Props) {
  const [form, setForm] = useState({
    ustaRank: user.ustaRank || '',
    utrRank: user.utrRank || '',
    pickleballRank: user.pickleballRank || '',
    tennisPrimaryLevel: user.tennisPrimaryLevel?.toString() || '',
    pickleballPrimaryLevel: user.pickleballPrimaryLevel?.toString() || '',
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleFieldChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      await onSave({
        ustaRank: form.ustaRank || null,
        utrRank: form.utrRank || null,
        pickleballRank: form.pickleballRank || null,
        tennisPrimaryLevel: form.tennisPrimaryLevel ? parseFloat(form.tennisPrimaryLevel) : null,
        pickleballPrimaryLevel: form.pickleballPrimaryLevel ? parseFloat(form.pickleballPrimaryLevel) : null,
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const handleCancel = () => {
    setForm({
      ustaRank: user.ustaRank || '',
      utrRank: user.utrRank || '',
      pickleballRank: user.pickleballRank || '',
      tennisPrimaryLevel: user.tennisPrimaryLevel?.toString() || '',
      pickleballPrimaryLevel: user.pickleballPrimaryLevel?.toString() || '',
    });
    setSaveError(null);
    onCancel();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Rankings & Levels</h2>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button onClick={handleCancel} disabled={isSaving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors disabled:opacity-50">
                <X className="h-4 w-4" /> Cancel
              </button>
              <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" /> {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button onClick={onEdit} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors">
                <Pencil className="h-4 w-4" /> Edit
              </button>
              <button onClick={onHardDelete} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-800 hover:bg-red-700 text-white rounded-lg transition-colors border border-red-700">
                Hard Delete
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && (
        <div className="px-4 py-2 bg-red-900/20 border border-red-800/50 rounded-lg text-sm text-red-400">{saveError}</div>
      )}

      {/* Tennis Rankings */}
      <div>
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">Tennis</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RankingField label="USTA Rank" value={user.ustaRank || ''} formValue={form.ustaRank} field="ustaRank" description="e.g., 3.5, 4.0, 4.5" isEditing={isEditing} onChange={handleFieldChange} />
          <RankingField label="UTR Rank" value={user.utrRank || ''} formValue={form.utrRank} field="utrRank" description="Universal Tennis Rating" isEditing={isEditing} onChange={handleFieldChange} />
          <RankingField label="Primary Level" value={user.tennisPrimaryLevel?.toString() || ''} formValue={form.tennisPrimaryLevel} field="tennisPrimaryLevel" description="Numeric level (e.g., 3.50)" isEditing={isEditing} onChange={handleFieldChange} />
        </div>
      </div>

      {/* Pickleball Rankings */}
      <div>
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">Pickleball</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <RankingField label="Pickleball Rank" value={user.pickleballRank || ''} formValue={form.pickleballRank} field="pickleballRank" description="e.g., 3.0, 3.5, 4.0" isEditing={isEditing} onChange={handleFieldChange} />
          <RankingField label="Primary Level" value={user.pickleballPrimaryLevel?.toString() || ''} formValue={form.pickleballPrimaryLevel} field="pickleballPrimaryLevel" description="Numeric level (e.g., 3.00)" isEditing={isEditing} onChange={handleFieldChange} />
        </div>
      </div>
    </div>
  );
}
